import { inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import type { CanActivateFn } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { AuthStateService } from '../services/auth-state.service';

interface MeResponse {
  id: number;
  username: string;
  email: string;
  roles: string[];
  [key: string]: unknown;
}

/**
 * Auth Guard to protect routes from unauthorized access.
 *
 * Verifies authentication with the server via /api/me endpoint.
 * Uses AuthStateService as a cache to minimize unnecessary API calls
 * while maintaining server as the single source of truth.
 *
 * **SECURITY**: Only users with ROLE_SUPER_ADMIN can access the admin interface.
 *
 * @example
 * ```typescript
 * // app.routes.ts
 * {
 *   path: 'dashboard',
 *   component: DashboardComponent,
 *   canActivate: [authGuard]
 * }
 * ```
 */
export const authGuard: CanActivateFn = (route, state) => {
  const http = inject(HttpClient);
  const router = inject(Router);
  const authState = inject(AuthStateService);

  // Quick check: if not authenticated, skip API call
  if (!authState.isAuthenticated()) {
    router.navigate(['/login'], {
      queryParams: { returnUrl: state.url },
    });
    return false;
  }

  // Verify with server (single source of truth)
  // Note: HTTP interceptor automatically adds /api prefix
  return http.get<MeResponse>('/me', { withCredentials: true }).pipe(
    map((user) => {
      // Verify user has ROLE_SUPER_ADMIN
      if (!user.roles || !user.roles.includes('ROLE_SUPER_ADMIN')) {
        // User authenticated but not super admin - redirect to unauthorized
        authState.clearAuthState();
        router.navigate(['/unauthorized'], {
          queryParams: { reason: 'insufficient_permissions' },
        });
        return false;
      }

      // Server confirmed authentication is valid AND user is super admin
      authState.setAuthenticated(true);
      return true;
    }),
    catchError((_error) => {
      // Server says not authenticated - clear cache and redirect
      authState.clearAuthState();

      router.navigate(['/login'], {
        queryParams: { returnUrl: state.url },
      });

      return of(false);
    }),
  );
};
